import { motion } from "framer-motion";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Cell,
  Legend,
} from "recharts";
import {
  Users,
  Building2,
  TrendingDown,
  AlertTriangle,
  ArrowRight,
  CheckCircle2,
  Landmark,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Cite, SourcesList, type Source } from "./article-kit";

// Population vs. ownership comparison (percentages).
// Sources are cited inline and in the Sources list below.
const gapData = [
  {
    label: "Mobile city, AL",
    population: 51,
    ownership: 18,
    note: "City pop. share vs. AL Black firm-ownership share (proxy)",
  },
  {
    label: "Mobile County, AL",
    population: 36,
    ownership: 18,
    note: "County pop. share vs. AL Black firm-ownership share",
  },
  {
    label: "Alabama",
    population: 27.5,
    ownership: 18.3,
    note: "Adult share vs. share of all firm owners (MBDA 2022)",
  },
  {
    label: "United States (employer firms)",
    population: 14,
    ownership: 3,
    note: "Black share of pop. vs. Black share of employer firms",
  },
];

const sources: Source[] = [
  {
    n: 1,
    url: "https://www.census.gov/quickfacts/fact/table/mobilecityalabama/PST045224",
    label:
      "U.S. Census Bureau — QuickFacts: Mobile city, Alabama (Black or African American alone, latest ACS)",
  },
  {
    n: 2,
    url: "https://www.census.gov/quickfacts/fact/table/mobilecountyalabama/PST045224",
    label:
      "U.S. Census Bureau — QuickFacts: Mobile County, Alabama (race & ethnicity)",
  },
  {
    n: 3,
    url: "https://censusdots.com/al/mobile/",
    label: "Census Dots — Mobile, AL demographics (2024 estimate)",
  },
  {
    n: 4,
    url: "https://www.mbda.gov/sites/default/files/media/document/2022/Alabama%20Fact%20Sheet.pdf",
    label:
      "U.S. Minority Business Development Agency (MBDA) — Alabama State of Minority Business Fact Sheet (2022)",
  },
  {
    n: 5,
    url: "https://www.census.gov/data/tables/2022/econ/abs/2022-abs-characteristics-of-businesses.html",
    label:
      "U.S. Census Bureau — 2022 Annual Business Survey: Characteristics of Businesses",
  },
  {
    n: 6,
    url: "https://www.census.gov/library/stories/2025/11/black-owned-businesses.html",
    label:
      "U.S. Census Bureau — Characteristics of Business Owners release (Nov 2025)",
  },
  {
    n: 7,
    url: "https://www.brookings.edu/articles/closing-the-black-employer-gap/",
    label: "Brookings — Closing the Black employer gap",
  },
  {
    n: 8,
    url: "https://cber.culverhouse.ua.edu/",
    label: "UA Center for Business & Economic Research (CBER) — Alabama small-business baselines",
  },
  {
    n: 9,
    url: "https://www.cityofmobile.org/departments/small-business-development/",
    label:
      "City of Mobile — Office of Small Business Development / DBE & Equal Business Opportunity Program",
  },
  {
    n: 10,
    url: "https://www.al.com/news/mobile/2014/06/mobile_to_commission_disparity.html",
    label: "AL.com (2014) — Mobile commissions DBE disparity study",
  },
];

const BlackBusinessGapArticle = () => {
  return (
    <div className="space-y-12">
      {/* Hero stat */}
      <motion.section
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="rounded-2xl border border-border bg-card/40 p-6 md:p-8"
      >
        <div className="flex items-center gap-2 mb-4">
          <Users className="h-4 w-4 text-primary" />
          <span className="text-xs font-semibold tracking-[0.18em] uppercase text-primary">
            Mobile, Alabama — Population vs. Ownership
          </span>
        </div>
        <div className="grid sm:grid-cols-2 gap-6">
          <div>
            <div className="text-5xl md:text-6xl font-display font-bold text-foreground">
              ~51%<Cite ns={[1, 3]} />
            </div>
            <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
              Share of Mobile <em>city</em> residents who are Black or African American — making
              Mobile one of the largest majority-Black cities in the Gulf South.
            </p>
          </div>
          <div>
            <div className="text-5xl md:text-6xl font-display font-bold text-foreground">
              18.3%<Cite ns={[4]} />
            </div>
            <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
              Black share of <em>all</em> Alabama firm owners (the most recent state-level figure
              available from MBDA's 2022 fact sheet). At the employer-firm level the gap is wider
              still.<Cite ns={[5, 7]} />
            </p>
          </div>
        </div>
      </motion.section>

      {/* Intro */}
      <section>
        <p className="text-lg text-muted-foreground leading-relaxed">
          Mobile is a majority-Black city inside a county where Black residents are roughly a
          third of the population.<Cite ns={[1, 2]} /> But that demographic weight is not
          reflected in who owns the businesses doing the city's work — bidding on contracts,
          hiring locally, building wealth that stays in Mobile. The gap is well-documented at
          every level of geography, and it is the gap Mobile's DBE programs and Contractor's
          College were built to close.
        </p>
      </section>

      {/* Chart */}
      <section>
        <h2 className="font-display text-2xl md:text-3xl font-bold text-foreground mb-3">
          The gap, at four levels of geography
        </h2>
        <p className="text-muted-foreground leading-relaxed mb-5">
          Black Alabamians are 27.5% of the adult population but only 18.3% of all firm
          owners.<Cite ns={[4]} /> Nationally, Black Americans are roughly 14% of the population
          but own about 3% of <em>employer</em> firms — businesses with at least one paid
          employee.<Cite ns={[5, 7]} /> Mobile sits inside both of those gaps.
        </p>
        <div className="rounded-xl border border-border bg-card/40 p-4 md:p-6">
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={gapData}
                margin={{ top: 12, right: 12, left: 0, bottom: 8 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis
                  dataKey="label"
                  stroke="hsl(var(--muted-foreground))"
                  fontSize={11}
                  tickMargin={6}
                />
                <YAxis
                  stroke="hsl(var(--muted-foreground))"
                  fontSize={11}
                  tickFormatter={(v) => `${v}%`}
                />
                <Tooltip
                  contentStyle={{
                    background: "hsl(var(--card))",
                    border: "1px solid hsl(var(--border))",
                    borderRadius: 8,
                    fontSize: 12,
                  }}
                  formatter={(value: number, name: string) => [`${value}%`, name]}
                />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="population" name="Black share of population" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
                <Bar dataKey="ownership" name="Black share of firm ownership" fill="hsl(var(--muted-foreground))" radius={[4, 4, 0, 0]}>
                  {gapData.map((_, i) => (
                    <Cell key={i} fill="hsl(var(--muted-foreground) / 0.6)" />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
          <p className="text-xs text-muted-foreground mt-3 leading-relaxed">
            Sources: U.S. Census Bureau QuickFacts for Mobile city and Mobile County;<Cite ns={[1, 2]} />{" "}
            MBDA Alabama State of Minority Business Fact Sheet (2022);<Cite ns={[4]} /> U.S. Census
            Annual Business Survey;<Cite ns={[5]} /> Brookings.<Cite ns={[7]} />
          </p>
        </div>
      </section>

      {/* Population side */}
      <section>
        <h2 className="font-display text-2xl md:text-3xl font-bold text-foreground mb-3">
          The population side
        </h2>
        <div className="grid md:grid-cols-2 gap-4">
          <div className="rounded-xl border border-border p-5">
            <div className="flex items-center gap-2 mb-2">
              <Badge variant="outline" className="text-[10px]">City of Mobile</Badge>
            </div>
            <p className="text-sm text-muted-foreground leading-relaxed">
              The U.S. Census Bureau's QuickFacts table for Mobile <strong>city</strong> reports
              that roughly half of all residents identify as Black or African American
              alone.<Cite ns={[1]} /> Independent 2024 estimates from Census Dots put the figure
              at 50.2%.<Cite ns={[3]} /> Either source supports the headline that Mobile is a
              majority-Black city.
            </p>
          </div>
          <div className="rounded-xl border border-border p-5">
            <div className="flex items-center gap-2 mb-2">
              <Badge variant="outline" className="text-[10px]">Mobile County</Badge>
            </div>
            <p className="text-sm text-muted-foreground leading-relaxed">
              The surrounding county is more demographically mixed: roughly 36% of Mobile County
              residents are Black or African American alone.<Cite ns={[2]} /> The wider you draw
              the boundary, the smaller the Black population share — and the larger the ownership
              gap looks relative to the city itself.
            </p>
          </div>
        </div>
      </section>

      {/* Ownership gap */}
      <section>
        <h2 className="font-display text-2xl md:text-3xl font-bold text-foreground mb-3">
          The ownership side
        </h2>
        <p className="text-muted-foreground leading-relaxed mb-4">
          The cleanest single statistic comes from the U.S. Minority Business Development Agency's
          2022 Alabama fact sheet: Black Alabamians are <strong>27.5% of the adult
          population</strong> but only <strong>18.3% of all firm owners</strong> in the state.<Cite ns={[4]} />{" "}
          That nine-point gap is statewide — and because Mobile city has a much higher Black
          population share than Alabama overall, the local gap is almost certainly larger.
        </p>
        <div className="grid md:grid-cols-3 gap-4">
          <div className="rounded-xl bg-primary/5 border border-primary/20 p-5">
            <TrendingDown className="h-5 w-5 text-primary mb-2" />
            <div className="text-2xl font-bold text-foreground">~3%</div>
            <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
              Black share of U.S. <em>employer</em> firms, against ~14% of the
              population.<Cite ns={[7]} />
            </p>
          </div>
          <div className="rounded-xl bg-primary/5 border border-primary/20 p-5">
            <Building2 className="h-5 w-5 text-primary mb-2" />
            <div className="text-2xl font-bold text-foreground">18.3%</div>
            <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
              Black share of <em>all</em> Alabama firm owners (MBDA 2022).<Cite ns={[4]} />
            </p>
          </div>
          <div className="rounded-xl bg-primary/5 border border-primary/20 p-5">
            <AlertTriangle className="h-5 w-5 text-primary mb-2" />
            <div className="text-2xl font-bold text-foreground">9 pts</div>
            <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
              Statewide gap between population share and firm-owner share.<Cite ns={[4]} />
            </p>
          </div>
        </div>
      </section>

      {/* Why */}
      <section>
        <h2 className="font-display text-2xl md:text-3xl font-bold text-foreground mb-3">
          Why the gap persists
        </h2>
        <ul className="space-y-3">
          <li className="flex gap-3 text-sm">
            <ArrowRight className="h-4 w-4 text-primary shrink-0 mt-0.5" />
            <span className="text-muted-foreground">
              <strong className="text-foreground">Capital access.</strong> Black-owned firms are
              disproportionately concentrated in nonemployer businesses (sole proprietors),
              which the Census Bureau's late-2025 release on business owners again documents.<Cite ns={[6]} />{" "}
              Moving from nonemployer to employer requires capital most local banks aren't extending.
            </span>
          </li>
          <li className="flex gap-3 text-sm">
            <ArrowRight className="h-4 w-4 text-primary shrink-0 mt-0.5" />
            <span className="text-muted-foreground">
              <strong className="text-foreground">Contracting access.</strong> Mobile commissioned a
              formal DBE disparity study a decade ago precisely because minority firms were winning
              far less city work than their availability suggested.<Cite ns={[10]} />
            </span>
          </li>
          <li className="flex gap-3 text-sm">
            <ArrowRight className="h-4 w-4 text-primary shrink-0 mt-0.5" />
            <span className="text-muted-foreground">
              <strong className="text-foreground">Banking disconnection.</strong> Mobile's Financial
              Empowerment Action Plan reports 8% unbanked and 17% underbanked households — a
              structural drag on the kind of cash-flow visibility lenders use to underwrite small
              businesses.
            </span>
          </li>
        </ul>
      </section>

      {/* What Mobile is doing */}
      <section>
        <h2 className="font-display text-2xl md:text-3xl font-bold text-foreground mb-3">
          What Mobile is already doing
        </h2>
        <div className="grid md:grid-cols-2 gap-4">
          <div className="rounded-xl border border-border p-5">
            <Landmark className="h-5 w-5 text-primary mb-2" />
            <h3 className="font-display font-semibold text-foreground mb-1">
              DBE & Equal Business Opportunity Program
            </h3>
            <p className="text-sm text-muted-foreground leading-relaxed">
              The City's Office of Small Business Development runs DBE certification and the
              Equal Business Opportunity Program — the two formal levers Mobile has for steering
              public contracting toward underrepresented firms.<Cite ns={[9]} />
            </p>
          </div>
          <div className="rounded-xl border border-border p-5">
            <Building2 className="h-5 w-5 text-primary mb-2" />
            <h3 className="font-display font-semibold text-foreground mb-1">
              Contractor's College
            </h3>
            <p className="text-sm text-muted-foreground leading-relaxed">
              The City's Contractor's College trains small and minority-owned firms in the
              bonding, estimating, and compliance work required to compete for public
              contracts.<Cite ns={[9]} />
            </p>
          </div>
          <div className="rounded-xl border border-border p-5">
            <CheckCircle2 className="h-5 w-5 text-primary mb-2" />
            <h3 className="font-display font-semibold text-foreground mb-1">
              Bank On Mobile coalition
            </h3>
            <p className="text-sm text-muted-foreground leading-relaxed">
              The Financial Empowerment Plan's Bank On coalition reported a 167% increase in new
              certified accounts between 2020 and 2021, directly addressing the unbanked share
              that holds back small-business credit.
            </p>
          </div>
          <div className="rounded-xl border border-border p-5">
            <Users className="h-5 w-5 text-primary mb-2" />
            <h3 className="font-display font-semibold text-foreground mb-1">
              MBDA & state ecosystem
            </h3>
            <p className="text-sm text-muted-foreground leading-relaxed">
              MBDA's Alabama fact sheet and UA's Center for Business & Economic Research provide
              the baseline data Mobile-area operators can cite when applying for capital,
              technical assistance, and grant programs.<Cite ns={[4, 8]} />
            </p>
          </div>
        </div>
      </section>

      {/* What businesses can do */}
      <section>
        <h2 className="font-display text-2xl md:text-3xl font-bold text-foreground mb-3">
          What small businesses can do this quarter
        </h2>
        <ul className="space-y-3">
          <li className="flex gap-3 text-sm">
            <CheckCircle2 className="h-4 w-4 text-primary shrink-0 mt-0.5" />
            <span className="text-muted-foreground">
              <strong className="text-foreground">Get DBE-certified through the City of Mobile.</strong>{" "}
              It is the single highest-leverage credential for unlocking local public-sector
              contract spend.<Cite ns={[9]} />
            </span>
          </li>
          <li className="flex gap-3 text-sm">
            <CheckCircle2 className="h-4 w-4 text-primary shrink-0 mt-0.5" />
            <span className="text-muted-foreground">
              <strong className="text-foreground">Register on SAM.gov.</strong> Without an active
              SAM.gov registration you cannot receive federal contracts, grants, or pass-through
              funds — including dollars that come through City and County programs.
            </span>
          </li>
          <li className="flex gap-3 text-sm">
            <CheckCircle2 className="h-4 w-4 text-primary shrink-0 mt-0.5" />
            <span className="text-muted-foreground">
              <strong className="text-foreground">Enroll in Contractor's College.</strong> Bonding
              and estimating capacity is what separates firms that can bid on city work from
              firms that can't.<Cite ns={[9]} />
            </span>
          </li>
          <li className="flex gap-3 text-sm">
            <CheckCircle2 className="h-4 w-4 text-primary shrink-0 mt-0.5" />
            <span className="text-muted-foreground">
              <strong className="text-foreground">Track active and proposed legislation</strong>{" "}
              that shapes set-aside thresholds, procurement rules, and capital access — and align
              your DBE positioning to it.
            </span>
          </li>
        </ul>
      </section>

      {/* Closing */}
      <section>
        <h2 className="font-display text-2xl md:text-3xl font-bold text-foreground mb-3">
          The bottom line
        </h2>
        <p className="text-muted-foreground leading-relaxed">
          Mobile's 51% Black population is not the story. The story is that the city already has
          the population, the formal DBE infrastructure, and the data baseline needed to close
          the gap — and the gap still closes only when individual small businesses get
          certified, bid on contracts, and convert nonemployer status into employer status. That
          is the work the next decade of Mobile's economy depends on.
        </p>
      </section>

      <SourcesList sources={sources} />
    </div>
  );
};

export default BlackBusinessGapArticle;
export const BLACK_BUSINESS_GAP_SLUG =
  "52-percent-black-population-business-ownership-gap";